import React, { FC, useEffect, useState } from 'react'
import { Button, Col, Divider, Form, Row } from 'antd'
import { arrayPush, Field, FieldArray, initialize, InjectedFormProps, reduxForm } from 'redux-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

// reducers
import { RootState } from '../../reducers'

// atoms
import SwitchField from '../../atoms/SwitchField'
import SelectField from '../../atoms/SelectField'
import InputField from '../../atoms/InputField'

// utils
import {
	CONDITIONS_TYPES,
	ELEMENT_ATTRIBUTES,
	ELEMENT_TYPES,
	ELEMENTS_WITH_VISIBILITY,
	FORM,
	VALIDATION_FORM_FIELD_ARRAYS,
	VALIDATION_MODAL_NUMBER_OF_ROWS
} from '../../utils/enums'

// interfaces
import { IFieldSqlEditor, IForm, IItem } from '../../types/interfaces'

// assets
import { ReactComponent as TrashIcon } from '../../assets/icons/BaseIcons/trash-1-icon.svg'
import SQLEditorModal from '../modals/SQLEditorModal'

export type ComponentProps = {
	onSubmit: any
	elementLabel: string
	elementType: ELEMENT_TYPES
	validationRequired?: any
	validationReadonly?: any
}

type Props = InjectedFormProps<IForm, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

export interface ICondition {
	key: string // (column in table for form element)
	value: string // (value from input)
	condition: string // (something from enum CONDITIONS_TYPES)
	type: string // (AND, OR)
}

export interface ContainerState {
	conditions: ICondition[]
}

const RenderOptions = ({ fields }: any) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const validableComponents = useSelector((state: RootState) => state.formTemplate.validableComponents)
	const formValues = useSelector((state: RootState) => state.form[FORM.VALIDATION_FORM].values?.[fields.name])
	const [conditionTypes, setConditionTypes] = useState<IItem[]>([])
	const [isSQLEditorModalShowed, setSQLEditorModalShowed] = useState<boolean>(false)
	// on init map condition types and set them into array
	useEffect(() => {
		setConditionTypes(Object.values(CONDITIONS_TYPES).map((item) => ({ label: item, value: item })))
	}, [])

	// save data from sql editor modal to fields array
	const saveQueryToField = (sqlEditorData: IFieldSqlEditor) => {
		if (!isEmpty(sqlEditorData?.data)) {
			fields.splice(sqlEditorData.index, 1, { ...fields.get(sqlEditorData.index), ...sqlEditorData.data })
		}
	}

	return (
		<>
			{fields?.map((item: ICondition, index: number) => (
				<Row key={index} justify={'center'} className={'my-2 w-full'}>
					<Col span={7} className={'flex flex-col justify-end selectField'}>
						<Field
							component={SelectField}
							placeholder={t('loc:Choose element')}
							name={`${item}.key`}
							loading={validableComponents?.isLoading}
							options={validableComponents?.data}
							size={'medium'}
							disabled={disable}
							clearBtn={true}
						/>
					</Col>
					<Col span={1} />
					<Col span={6} className={'flex flex-col justify-end selectField'}>
						<Field
							component={SelectField}
							name={`${item}.condition`}
							placeholder={t('loc:Choose condition')}
							options={conditionTypes}
							size={'medium'}
							disabled={disable || formValues?.[index]?.sqlSwitch}
							clearBtn={true}
						/>
					</Col>
					<Col span={1} />
					{formValues?.[index]?.sqlSwitch ? (
						<Col span={5} className={'flex flex-col justify-end selectField'}>
							<Button
								className={'unfilled-btn w-full h-25px flex items-center justify-center'}
								disabled={disable}
								onClick={() => {
									setSQLEditorModalShowed(true)
									dispatch(
										initialize(FORM.SQL_EDITOR_FORM, {
											[ELEMENT_ATTRIBUTES.SQL]: formValues?.[index]?.[ELEMENT_ATTRIBUTES.SQL] ?? '',
											[ELEMENT_ATTRIBUTES.LINKED_FIELDS]: formValues?.[index]?.[ELEMENT_ATTRIBUTES.LINKED_FIELDS],
											// add index for pass it to sql editor form => update specify filed with saveQueryToField function
											index
										})
									)
								}}
							>
								{t('loc:SQL editor')}
							</Button>
						</Col>
					) : (
						<Col span={5} className={'flex flex-col justify-end selectField'}>
							<Field component={InputField} name={`${item}.value`} size={'medium'} disabled={disable} />
						</Col>
					)}
					<Col span={2} className={'flex flex-col justify-end items-center'}>
						<Field component={SwitchField} name={`${item}.sqlSwitch`} className={'float-right m-0 switchButton'} disabled={disable} />
					</Col>
					<Col span={2} className={'flex flex-col justify-end items-center'}>
						<Button
							className={'flex items-center justify-center w-8 h-8 p-1 border-0'}
							onClick={() => {
								if (fields.get(index) && Object.keys(fields.get(index)).length > 0) {
									fields.remove(index)
								}
							}}
							icon={<TrashIcon />}
						/>
					</Col>
				</Row>
			))}
			<SQLEditorModal
				width={460}
				typeOfElementForm={FORM.VALIDATION_FORM}
				saveQueryToFields={saveQueryToField}
				isVisible={isSQLEditorModalShowed}
				onCancel={() => {
					setSQLEditorModalShowed(false)
				}}
			/>
		</>
	)
}

const ValidationForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const { handleSubmit, elementLabel, elementType } = props
	const disable = useSelector((state: RootState) => state.formTemplate.disable)

	const textForElementTypes: { [key: string]: string } = {
		[`${ELEMENT_TYPES.INPUT}`]: t('loc:Input'),
		[`${ELEMENT_TYPES.TEXT_AREA}`]: t('loc:Text area'),
		[`${ELEMENT_TYPES.DROPDOWN_BUTTON}`]: t('loc:Dropdown'),
		[`${ELEMENT_TYPES.DATE_PICKER}`]: t('loc:Date/Time picker'),
		[`${ELEMENT_TYPES.RADIO_BUTTON}`]: t('loc:Radio'),
		[`${ELEMENT_TYPES.CHECK_BOX}`]: t('loc:Checkbox'),
		[`${ELEMENT_TYPES.TEXT}`]: t('loc:Paragraph'),
		[`${ELEMENT_TYPES.BUTTON}`]: t('loc:Button'),
		[`${ELEMENT_TYPES.TABLE}`]: t('loc:Table'),
		[`${ELEMENT_TYPES.TITLE}`]: t('loc:Title'),
		[`${ELEMENT_TYPES.IMAGE}`]: t('loc:Image'),
		[`${ELEMENT_TYPES.SELECT_FROM_TABLE}`]: t('loc:Select from table'),
		[`${ELEMENT_TYPES.TAB_BOX}`]: t('loc:Tab panel')
	}

	return (
		<Form layout={'vertical'} onSubmitCapture={handleSubmit} className={'w-full validation-form'}>
			<Row justify={'center'} className={'my-2 w-full'}>
				<Col span={3}>
					<p className={'m-0 text-gray-600'}>{t('loc:Element type')}</p>
				</Col>
				<Col span={1} />
				<Col span={11}>
					<p className={'m-0 font-bold'}>{textForElementTypes?.[elementType]}</p>
				</Col>
				<Col span={!ELEMENTS_WITH_VISIBILITY.includes(elementType) ? 1 : 9} />
				{!ELEMENTS_WITH_VISIBILITY.includes(elementType) && (
					<>
						<Col span={3}>
							<p className={'m-0 text-gray-600'}>{t('loc:Required')}</p>
						</Col>
						<Col span={4} />
						<Col span={1} className={'h-34px'}>
							<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.REQUIRED} className={'float-right m-0 switchButton'} disabled={disable} />
						</Col>
					</>
				)}
			</Row>
			<Row justify={'center'} className={'my-2 w-full'}>
				<Col span={3}>
					<p className={'m-0 text-gray-600'}>{t('loc:Element label')}</p>
				</Col>
				<Col span={1} />
				<Col span={11}>
					<p className={'m-0 font-bold '}>{elementLabel}</p>
				</Col>
				<Col span={!ELEMENTS_WITH_VISIBILITY.includes(elementType) ? 1 : 9} />
				{!ELEMENTS_WITH_VISIBILITY.includes(elementType) && (
					<>
						<Col span={3}>
							<p className={'m-0 text-gray-600'}>{t('loc:Readonly')}</p>
						</Col>
						<Col span={4} />
						<Col span={1} className={'h-34px'}>
							<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.READ_ONLY} className={'float-right m-0 switchButton'} disabled={disable} />
						</Col>
					</>
				)}
			</Row>
			<Divider className={'my-2'} />
			{!ELEMENTS_WITH_VISIBILITY.includes(elementType) && (
				<Row justify={'start'} className={'my-4 w-full flex-col'}>
					<h3>{t('loc:Required if')}</h3>
					<span>{t('loc:All these conditions must be met')}</span>
					<FieldArray name={VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_AND_OPTIONS} component={RenderOptions} />
					<span>{t('loc:Or one of the following')}</span>
					<FieldArray name={VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_OR_OPTIONS} component={RenderOptions} />
				</Row>
			)}
			<Row justify={'start'} className={'my-4 w-full flex-col'}>
				<h3>{t('loc:Dynamic visibility')}</h3>
				<span>{t('loc:All these conditions must be met')}</span>
				<FieldArray name={VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS} component={RenderOptions} />
				<span>{t('loc:Or one of the following')}</span>
				<FieldArray name={VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS} component={RenderOptions} />
			</Row>
		</Form>
	)
}

const checkEmptyRow = (array: any[]): boolean => {
	let result = true
	// check if field array's length is less than 5 = user can add max 5 rows
	if (array?.length >= VALIDATION_MODAL_NUMBER_OF_ROWS) {
		result = false
	} else {
		array?.forEach((option) => {
			if (option && Object.keys(option)?.length === 0) {
				result = false
			}
		})
	}
	return result
}

const form = reduxForm<IForm, ComponentProps>({
	form: FORM.VALIDATION_FORM,
	forceUnregisterOnUnmount: false,
	touchOnChange: true,
	destroyOnUnmount: false,
	onChange: (values: any, dispatch) => {
		// if fields array's length is less than 5 && then dispatch form if user select/insert value => then add new row to field array, works ONLY if is set max number of rows
		if (checkEmptyRow(values?.[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_AND_OPTIONS])) {
			dispatch(arrayPush(FORM.VALIDATION_FORM, VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_AND_OPTIONS, {}))
		}
		if (checkEmptyRow(values?.[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_OR_OPTIONS])) {
			dispatch(arrayPush(FORM.VALIDATION_FORM, VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_OR_OPTIONS, {}))
		}
		if (checkEmptyRow(values?.[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS])) {
			dispatch(arrayPush(FORM.VALIDATION_FORM, VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS, {}))
		}
		if (checkEmptyRow(values?.[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS])) {
			dispatch(arrayPush(FORM.VALIDATION_FORM, VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS, {}))
		}
	}
})(ValidationForm)

export default form
